import PropTypes from 'prop-types'
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell
} from '@table-library/react-table-library/table'
import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Button
} from '@material-tailwind/react'
import { useTheme } from '@table-library/react-table-library/theme'
import moment from '@/config/moment'
import { FiDownloadCloud, FiUpload } from 'react-icons/fi'
import { useEffect, useMemo, useState } from 'react'
import { attachmentChipStatuses } from '@/constants/attachment-chip-statuses'
import ShowMoreText from 'react-show-more-text'
import { StatusChip } from '@/components/status-chip'
import { LoadingRow } from '@/shared/components/tables'
import { ATTACHMENT_STATUSES } from '@/constants'
import { Link, createSearchParams } from 'react-router-dom'
import { tabOptions } from './constants'

export const AttachmentTableWithTabs = ({
  getMore,
  initialSearchParams,
  buildUploadPath,
  initialTab,
  onUploadNavigateStart
}) => {
  const defaultTab = tabOptions[0].key
  const resolvedInitialTab = tabOptions.some(({ key }) => key === initialTab)
    ? initialTab
    : defaultTab
  const [canFetchMore, setCanFetchMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [nodes, setNodes] = useState({ nodes: [] })
  const [currentTab, setCurrentTab] = useState(resolvedInitialTab)
  const lockedParams = useMemo(() => ({
    ...initialSearchParams,
    page: 1,
    per_page: 10
  }), [initialSearchParams])
  const [params, setParams] = useState(lockedParams)

  const getParamsByTab = ({ tabKey, params = {} }) => {
    const newParams = Object.assign({}, params)

    if (tabKey === 'restStatus') {
      newParams['status[]'] = [
        ATTACHMENT_STATUSES.pendingUpload,
        ATTACHMENT_STATUSES.rejected,
        ATTACHMENT_STATUSES.expired
      ]
    } else {
      newParams['status[]'] = tabKey
    }

    return newParams
  }

  const onTabChange = (tabKey) => {
    setCurrentTab(tabKey)
  }

  const loadMore = async ({
    reset = false,
    tabKey = currentTab,
    requestParams = params
  } = {}) => {
    setIsLoading(true)
    try {
      const newSearchParams = createSearchParams(
        getParamsByTab({ tabKey, params: requestParams })
      )
      const { attachments, hasMore } = await getMore(newSearchParams)
      setNodes((prevNodes) => ({
        nodes: reset ? attachments : [...prevNodes.nodes, ...attachments]
      }))

      const newParams = {
        ...requestParams,
        page: requestParams.page + 1
      }

      setParams(newParams)
      setCanFetchMore(hasMore)
    } catch (error) {
      console.log('error', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setNodes({ nodes: [] })
    setCanFetchMore(false)
    setParams(lockedParams)
    loadMore({
      reset: true,
      tabKey: currentTab,
      requestParams: lockedParams
    })
  }, [currentTab])

  const theme = useTheme({
    Table: `
        --data-table-library_grid-template-columns: 3fr repeat(3, 1fr) 4fr 0.1fr;
      `
  })

  const approvedTheme = useTheme({
    Table: `
        --data-table-library_grid-template-columns: 3fr repeat(4, 1fr) 4fr 0.1fr;
      `
  })

  const formatUploadedAt = (value) => {
    if (!value) return '-'

    const parsedDate = moment(value)
    return parsedDate.isValid() ? parsedDate.format('L') : '-'
  }

  const formatExpirationDate = (value) => {
    if (!value) return '-'

    const parsedDate = moment(value)
    return parsedDate.isValid() ? parsedDate.format('L') : '-'
  }

  const canUploadAttachment = (attachment) => {
    const recurrence =
      attachment?.attachmentType?.recurrence ||
      attachment?.attachment_type?.recurrence ||
      attachment?.recurrence
    const hasDocument = Boolean(attachment?.documentInfo?.url)

    if (hasDocument) return false
    if (attachment?.status === ATTACHMENT_STATUSES.notApplicable) return false

    if (
      attachment?.status === ATTACHMENT_STATUSES.approved &&
      ['termination', 'monthly'].includes(recurrence)
    ) {
      return false
    }

    return true
  }

  return (
    <Tabs value={currentTab}>
      <TabsHeader>
        {tabOptions.map(({ label, key }) => (
          <Tab key={key} value={key} onClickCapture={() => onTabChange(key)}>
            {label}
          </Tab>
        ))}
      </TabsHeader>
      <TabsBody>
        <TabPanel value={currentTab} className=''>
          <Table
            key={currentTab}
            layout={{ custom: true }}
            theme={currentTab === ATTACHMENT_STATUSES.approved ? approvedTheme : theme}
            data={nodes}
            className='max-h-80 mt-4 overflow-y'
          >
            {(tableList) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCell>Tipo</HeaderCell>
                    <HeaderCell>Periodo</HeaderCell>
                    <HeaderCell>Fecha de carga</HeaderCell>
                    {currentTab === ATTACHMENT_STATUSES.approved && (
                      <HeaderCell>Fecha de expiración</HeaderCell>
                    )}
                    <HeaderCell>Estado</HeaderCell>
                    <HeaderCell>Comentarios</HeaderCell>
                    <HeaderCell>Acciones</HeaderCell>
                  </HeaderRow>
                </Header>

                <Body>
                  {isLoading && <LoadingRow gridColumnEnd={currentTab === ATTACHMENT_STATUSES.approved ? 8 : 7} />}
                  {!isLoading && !nodes.nodes.length && (
                    <Row >
                      <Cell className='flex flex-col' gridColumnStart={1} gridColumnEnd={currentTab === ATTACHMENT_STATUSES.approved ? 8 : 7}>No hay información disponible</Cell>
                    </Row>
                  )}
                  {tableList.map((attachment) => (
                    <Row key={attachment.id} item={attachment}>
                      <Cell>
                        <div className='text-wrap'>
                          {attachment.attachmentType.name}
                        </div>
                      </Cell>
                      <Cell>{attachment.period}</Cell>
                      <Cell>{formatUploadedAt(attachment.documentUploadedAt)}</Cell>
                      {currentTab === ATTACHMENT_STATUSES.approved && (
                        <Cell>{formatExpirationDate(attachment.expirationDate)}</Cell>
                      )}
                      <Cell>
                        <StatusChip
                          options={attachmentChipStatuses}
                          value={attachment.status}
                        />
                      </Cell>
                      <Cell class='px-1 py-1'>
                        <ShowMoreText
                          lines={2}
                          more={
                            <a className='underline underline-offset-2 text-blue-600'>
                              Ver más
                            </a>
                          }
                          less={
                            <a className='underline underline-offset-2 text-blue-600'>
                              Ver menos
                            </a>
                          }
                        >
                          {attachment?.comment?.body}
                        </ShowMoreText>
                      </Cell>
                      <Cell>
                        <div className='flex items-center gap-2'>
                          {buildUploadPath && canUploadAttachment(attachment) && (
                            <Link
                              to={buildUploadPath(attachment, currentTab)}
                              onClick={() => onUploadNavigateStart?.()}
                            >
                              <FiUpload />
                            </Link>
                          )}
                          {attachment?.documentInfo?.url ? (
                            <a
                              href={attachment.documentInfo.url}
                              target='_blank'
                              rel='noreferrer'
                            >
                              <FiDownloadCloud />
                            </a>
                          ) : (
                            !buildUploadPath && '-'
                          )}
                        </div>
                      </Cell>
                    </Row>
                  ))}
                  {
                    canFetchMore && (
                      <Row>
                        <Cell className='border-none flex flex-col' gridColumnStart={1} gridColumnEnd={currentTab === ATTACHMENT_STATUSES.approved ? 8 : 7}>
                          <Button loading={isLoading} variant='outlined' color='white' onClick={loadMore}>Cargar más</Button>
                        </Cell>
                      </Row>
                    )
                  }
                </Body>
              </>
            )}
          </Table>
        </TabPanel>
      </TabsBody>
    </Tabs>
  )
}

AttachmentTableWithTabs.defaultProps = {
  attachments: [],
  isLoading: false,
  initialTab: null,
  onUploadNavigateStart: null
}

AttachmentTableWithTabs.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.object),
  getMore: PropTypes.func,
  initialSearchParams: PropTypes.object,
  initialTab: PropTypes.string,
  onUploadNavigateStart: PropTypes.func
}
